/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import s from './Navbar.module.scss'
import { Container } from '@corratech/pylot-ui'
import { Searchbar, UserNav } from '@components/common'
import cn from 'classnames'
import { MegaMenu } from '@corratech/pylot-megamenu'
import { useTranslation } from 'next-i18next/pages'
import { Logo } from '@components/brand'
import { PWALogo } from '@components/icons'
import CloseSearch from '@components/icons/CloseSearch'
import { elementIsOnScreen, subMenuAction } from '@lib/utils/DOM'
import FlagChile from '@components/icons/FlagChile'
import FlagColombia from '@components/icons/FlagColombia'
import FlagPeru from '@components/icons/FlagPeru'
import FlagBolivia from '@components/icons/FlagBolivia'
import FlagMexico from '@components/icons/FlagMexico'
import { useIsMobileScreen } from '@lib/utils/mediaQuery'
import { useRouter } from 'next/router'

/**
 * Is used to query element by id and get navbar's height as an offset
 * when scrolling to the target element.
 */
export const NAVBAR_ID = 'navbar'

const FLAGS = [
    {
        name: 'Chile',
        flagComponent: <FlagChile />,
        url: ''
    },
    {
        name: 'Bolivia',
        flagComponent: <FlagBolivia />,
        url: 'https://www.casaideas.com.bo/'
    },
    {
        name: 'Colombia',
        flagComponent: <FlagColombia />,
        url: 'https://www.casaideas.com.co/'
    },
    {
        name: 'Perú',
        flagComponent: <FlagPeru />,
        url: 'https://www.casaideas.com.pe/'
    },
    {
        name: 'México',
        flagComponent: <FlagMexico />,
        url: 'https://www.casaideas-mexico.mx/'
    }
]

const isInsideOrSame = (root: HTMLElement | null, node: EventTarget | null) => {
    if (!root || !node || !(node instanceof Node)) return false
    return root === node || root.contains(node)
}

const hideAllSubMenus = () => {
    const subMenus = document.querySelectorAll<HTMLElement>(
        '.menu-cms-side-content'
    )

    const subMenuOption =
        document.querySelectorAll<HTMLElement>('.submenu-option')

    subMenus.forEach((menu) => {
        if (menu.classList.contains('block')) {
            menu.classList.replace('block', 'hidden')
        }
    })

    subMenuOption.forEach((menuOption) => {
        if (menuOption.classList.contains('menu-item-hovered')) {
            menuOption.classList.remove('menu-item-hovered')
        }
    })
}

/** El primer banner de la home (hero). Mientras tape el header, este va sin fondo. */
const heroStillCoversHeader = (): boolean => {
    const hero = document.querySelector<HTMLElement>(
        '.home-hero-slider, .pagebuilder-slider.hero-banner, .hero-banner.pagebuilder-slider'
    )
    if (!hero) return false
    return hero.getBoundingClientRect().bottom > 1
}

const Navbar: FC = () => {
    const [display, setDisplay] = useState(false)
    const { t } = useTranslation('common')
    const { pathname } = useRouter()
    const isHome = pathname === '/'

    const isMobile = useIsMobileScreen()
    const checkoutPath = '/checkout'
    const [isHidden, setIsHidden] = useState(false)
    const [scrolledUp, setScrolledUp] = useState(false)
    const [overHero, setOverHero] = useState(false)
    const [isSearchFocused, setIsSearchFocused] = useState(false)
    const [hasSearchValue, setHasSearchValue] = useState(false)
    const closeAutocompleteRef = useRef<(() => void) | null>(null)
    const shouldHideNav =
        isHidden &&
        (isHome ||
            pathname.includes('/categoria') ||
            (pathname.includes('/producto') && !isMobile))

    useEffect(() => {
        let lastScrollY = window.scrollY

        const handleScroll = () => {
            const currentScrollY = window.scrollY
            const scrollingDown =
                currentScrollY > lastScrollY && currentScrollY > 50

            if (scrollingDown) {
                setIsHidden(true)
                setScrolledUp(false)
            } else {
                setIsHidden(false)
                setScrolledUp(
                    isHome &&
                        currentScrollY < lastScrollY &&
                        currentScrollY > 8 &&
                        !heroStillCoversHeader()
                )
            }

            if (currentScrollY <= 8) setScrolledUp(false)

            setOverHero(isHome && heroStillCoversHeader())
            lastScrollY = currentScrollY
        }

        setOverHero(isHome && heroStillCoversHeader())
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [isHome])

    useEffect(() => {
        if (isMobile) return

        let closeTimer: ReturnType<typeof setTimeout> | null = null

        const cancelScheduledClose = () => {
            if (closeTimer) {
                clearTimeout(closeTimer)
                closeTimer = null
            }
        }

        const scheduleClose = (subMenu: HTMLElement, optionEl: HTMLElement) => {
            cancelScheduledClose()
            closeTimer = setTimeout(() => {
                if (subMenu.classList.contains('block')) {
                    subMenu.classList.replace('block', 'hidden')
                }
                optionEl.classList.remove('menu-item-hovered')
                closeTimer = null
            }, 520)
        }

        const ac = new AbortController()
        const { signal } = ac
        const addListenerOpts = { signal } as AddEventListenerOptions

        elementIsOnScreen('.submenu-option', (elements) => {
            elements.forEach((el) => {
                const opt = el as HTMLElement

                opt.addEventListener(
                    'mouseenter',
                    () => {
                        cancelScheduledClose()
                        const subMenuToShow = subMenuAction(opt.classList)
                        hideAllSubMenus()
                        if (subMenuToShow) {
                            opt.classList.add('menu-item-hovered')
                            subMenuToShow.classList.replace('hidden', 'block')
                        }
                    },
                    addListenerOpts
                )

                opt.addEventListener(
                    'mouseleave',
                    (e) => {
                        const subMenuToShow = subMenuAction(opt.classList)
                        if (!subMenuToShow) return
                        const rel = e.relatedTarget
                        if (isInsideOrSame(subMenuToShow, rel)) return
                        scheduleClose(subMenuToShow, opt)
                    },
                    addListenerOpts
                )
            })

            document
                .querySelectorAll<HTMLElement>('.menu-cms-side-content')
                .forEach((panel) => {
                    panel.addEventListener(
                        'mouseenter',
                        cancelScheduledClose,
                        addListenerOpts
                    )
                    panel.addEventListener(
                        'mouseleave',
                        (e) => {
                            const jsClass = [...panel.classList].find((c) =>
                                c.includes('js')
                            )
                            let optionEl = jsClass
                                ? (document.querySelector(
                                      `.submenu-option.${CSS.escape(jsClass)}`
                                  ) as HTMLElement | null)
                                : null
                            if (!optionEl) {
                                optionEl = document.querySelector(
                                    '.submenu-option.menu-item-hovered'
                                ) as HTMLElement | null
                            }
                            if (
                                !optionEl ||
                                subMenuAction(optionEl.classList) !== panel
                            ) {
                                return
                            }
                            const rel = e.relatedTarget
                            if (isInsideOrSame(optionEl, rel)) return
                            scheduleClose(panel, optionEl)
                        },
                        addListenerOpts
                    )
                })
        })

        return () => {
            ac.abort()
            cancelScheduledClose()
        }
    }, [isMobile])

    const handleSearchFocus = () => {
        setIsSearchFocused(true)
    }

    const handleSearchBlur = () => {
        setIsSearchFocused(false)
    }

    const handleSearchValueChange = (value: string) => {
        setHasSearchValue(value.trim().length > 0)
    }

    const shouldHideUserNav = isSearchFocused || hasSearchValue

    return (
        <div
            className={cn(s.root, s['shadow-magical'], [s.navSticky], {
                [s.navHidden]: shouldHideNav,
                [s.isHome]: isHome,
                [s.scrolledUp]: isHome && scrolledUp,
                [s.overHero]: isHome && overHero
            })}
        >
            {pathname == checkoutPath ? (
                <Container
                    id={NAVBAR_ID}
                    clean
                    className="flex items-center justify-center"
                >
                    <Link
                        href="/"
                        className={s.logoContainerMobile}
                        aria-label={t('Logo Mobile')}
                    >
                        <PWALogo />
                    </Link>
                    <Link
                        href="/"
                        className={s.logoContainerDesktop}
                        aria-label={t('Logo Desktop')}
                    >
                        <Logo />
                    </Link>
                </Container>
            ) : (
                <Container id={NAVBAR_ID} clean className={s['nav-container']}>
                    <div className={s['nav-container-inner']}>
                        <div className={s['nav-section']}>
                            <MegaMenu className={s['megamenu-cms']} />
                            <Link
                                href="/"
                                className={s.logoContainerMobile}
                                aria-label={t('Logo Mobile')}
                            >
                                <PWALogo />
                            </Link>
                            <Link
                                href="/"
                                className={s.logoContainerDesktop}
                                aria-label={t('Logo Desktop')}
                            >
                                <Logo />
                            </Link>
                        </div>
                        <div className={s['nav-container-bottom']}>
                            <MegaMenu className={s['megamenu-cms']} />
                            <div
                                className={cn(
                                    s['mobile-search-icon'],
                                    'mobile-search-icon'
                                )}
                                data-gtm="mobile-search"
                            >
                                <Searchbar
                                    id="mobile-search"
                                    variant="header"
                                    onInputFocus={handleSearchFocus}
                                    onInputBlur={handleSearchBlur}
                                    onInputChange={handleSearchValueChange}
                                    closeAutocompleteRef={closeAutocompleteRef}
                                />
                            </div>
                        </div>
                        {shouldHideUserNav && (
                            <button
                                aria-label={t('Close search')}
                                className={s['close-search-button']}
                                onClick={() => {
                                    closeAutocompleteRef.current?.()
                                    setIsSearchFocused(false)
                                    setHasSearchValue(false)
                                }}
                            >
                                <CloseSearch />
                            </button>
                        )}
                        {!shouldHideUserNav && <UserNav />}
                    </div>
                </Container>
            )}
        </div>
    )
}

export default Navbar
